/**
 * GEOGRAPHY THUNKS — Lazy load + cache "load-once" tramite condition.
 *
 * `fetchCountries`: dispatch idempotente — se i countries sono già loading
 * o succeeded, il thunk viene saltato (Felix-style "no-op cache hit").
 *
 * `fetchRegionsForCountry(countryId)`: stesso pattern, ma indicizzato per
 * countryId. Ogni countryId ha il proprio `regionsStatus[countryId]`.
 */

import { createAsyncThunk } from '@reduxjs/toolkit';
import type { RootState } from '../../store/store';
import { extractError } from '../../store/thunkHelpers';
import { geographyApi } from './geographyApi';
import type { Country, Region } from './geographyTypes';

export const fetchCountries = createAsyncThunk<
  Country[],
  void,
  { state: RootState; rejectValue: string }
>(
  'geography/fetchCountries',
  async (_, { rejectWithValue }) => {
    try {
      return await geographyApi.fetchCountries();
    } catch (err: unknown) {
      return rejectWithValue(extractError(err));
    }
  },
  {
    condition: (_, { getState }) => {
      const status = getState().geography.countriesStatus;
      return status !== 'loading' && status !== 'succeeded';
    },
  },
);

export const fetchRegionsForCountry = createAsyncThunk<
  Region[],
  number,
  { state: RootState; rejectValue: string }
>(
  'geography/fetchRegionsForCountry',
  async (countryId, { rejectWithValue }) => {
    try {
      return await geographyApi.fetchRegionsByCountry(countryId);
    } catch (err: unknown) {
      return rejectWithValue(extractError(err));
    }
  },
  {
    condition: (countryId, { getState }) => {
      const status = getState().geography.regionsStatus[countryId];
      return status !== 'loading' && status !== 'succeeded';
    },
  },
);
